import {
  PROSPECT_OFFER_PHASE,
  PROSPECT_PHASES,
  type Prospect,
  type ProspectPhase,
  type ProspectStatus,
} from '@immo/api-contract';
import type { StatusBadgeTone } from '@immo/ui-kit';
import type { PhaseHue } from '../deals/phase';

// Phase → tag hue, the design handoff's table. The terminal PURCHASED phase carries
// no hue — `PhaseTag` renders a null hue as terminal-success.
export const PROSPECT_PHASE_HUE: Record<ProspectPhase, PhaseHue | null> = {
  INTEREST: 5,
  WITHDRAWAL_NOTICE_CONFIRMED: 5,
  CONTACTED: 4,
  VIEWING_PROPOSED: 4,
  VIEWING_SCHEDULED: 4,
  VIEWING_COMPLETED: 3,
  OFFER_RECEIVED: 2,
  FINANCING_CONFIRMED: 2,
  RESERVED: 6,
  NOTARY_APPOINTMENT: 6,
  PURCHASED: null,
};

export const PROSPECT_NOTARY_PHASE = 'NOTARY_APPOINTMENT' satisfies ProspectPhase;

export const PROSPECT_STATUS_TONE: Record<ProspectStatus, StatusBadgeTone> = {
  ACTIVE: 'info',
  WON: 'success',
  LOST: 'danger',
};

export function nextProspectPhase(phase: ProspectPhase): ProspectPhase | null {
  return PROSPECT_PHASES[PROSPECT_PHASES.indexOf(phase) + 1] ?? null;
}

/** The phases an Interessent in `phase` can be reset to, in pipeline order. */
export function earlierProspectPhases(phase: ProspectPhase): ReadonlyArray<ProspectPhase> {
  return PROSPECT_PHASES.slice(0, PROSPECT_PHASES.indexOf(phase));
}

/** An active Interessent past the first phase: the only one with somewhere to go back to. */
export function canRevertFrom(prospect: Prospect): boolean {
  return prospect.status === 'ACTIVE' && prospect.phase !== PROSPECT_PHASES[0];
}

/** Another Interessent holds the Objekt's reservation. */
export function isReservedByOther(prospect: Prospect): boolean {
  const { reservation } = prospect.property;

  return reservation !== null && reservation.prospectId !== prospect.id;
}

const hasBuyerOffer = (prospect: Prospect): boolean => prospect.offers.some((offer) => offer.kind === 'OFFER');

/** The move needs an Angebot of the buyer the Interessent does not have yet. */
export function needsOfferDialog(prospect: Prospect, toPhase: ProspectPhase): boolean {
  return PROSPECT_PHASES.indexOf(toPhase) >= PROSPECT_PHASES.indexOf(PROSPECT_OFFER_PHASE) && !hasBuyerOffer(prospect);
}
